import dayjs from 'dayjs';
import localeList from 'dayjs/locale.json';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import utc from 'dayjs/plugin/utc';
import { DEFAULT_DATE_FORMAT, DEFAULT_TIME_FORMAT, DEFAULT_WEEK_START_DAY, ISO_8601 } from "./constants";
dayjs.extend(customParseFormat);
dayjs.extend(localeData);
dayjs.extend(utc);
/**
 * Returns the best-matching key from dayjs/locale.json
 * @param locale{string}
 * @return {string}
 */

export var getDayjsLocale = function getDayjsLocale(locale) {
  locale = locale.toLowerCase();
  var foundLocale = localeList.find(function (loc) {
    return loc.key === locale;
  }); // Fallback to just the first part of the locale
  // e.g. if it doesn't find 'en-XX' fall back to 'en'

  if (!foundLocale) {
    foundLocale = localeList.find(function (loc) {
      return loc.key === locale.split('-')[0];
    });
  }

  return foundLocale ? foundLocale.key : undefined;
};
export var getDateFormat = function getDateFormat(locale) {
  if (locale === undefined || locale === ISO_8601) return DEFAULT_DATE_FORMAT;
  var foundLocale = getDayjsLocale(locale);
  if (!foundLocale) return DEFAULT_DATE_FORMAT;

  var dayjsLocale = require("dayjs/locale/".concat(foundLocale, ".js")); // Special case for 'en' because it's the default of dayjs and
  // is not treated like a normal locale


  if (dayjsLocale.name === 'en') return 'MM/DD/YYYY';
  return dayjs().locale(dayjsLocale).localeData().longDateFormat('L');
};
export var getWeekStartDay = function getWeekStartDay(locale) {
  if (locale === undefined) {
    return DEFAULT_WEEK_START_DAY;
  } else if (locale === ISO_8601) {
    return 1; // First day of week is Monday in ISO 8601
  }

  var foundLocale = getDayjsLocale(locale);
  if (!foundLocale) return DEFAULT_WEEK_START_DAY;

  var dayjsLocale = require("dayjs/locale/".concat(foundLocale, ".js")); // Special case for 'en' because it's the default of dayjs and
  // is not treated like a normal locale


  if (dayjsLocale.name === 'en') return DEFAULT_WEEK_START_DAY;
  return dayjs().locale(dayjsLocale).localeData().firstDayOfWeek();
};
export var getSeparator = function getSeparator(format) {
  return format[format.search(/[^DMY]/)];
};
export var stringToDate = function stringToDate(srcStr) {
  var format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_DATE_FORMAT;
  if (!srcStr) return undefined; // Insert symbols if the date string may not have them

  var str = srcStr.trim();

  if (!srcStr.match(/[^a-zA-Z0-9]/)) {
    var symbolsCount = 0;
    str = format.split('').map(function (fChar, index) {
      if (fChar.match(/[a-zA-Z0-9]/)) {
        return srcStr[index - symbolsCount];
      } else {
        symbolsCount++;
        return fChar;
      }
    }).join('');
  } // Third argument is used to enable strict parsing which requires that the format and input match exactly, including delimiters.


  var date = dayjs(str, format, true).utc(true).toDate(); // Happens when date is not valid, also when there are no leading 0's.
  // We want to treat e.g. 3/5/2020 as a valid US date, but the dayjs format expects 03/05/2020

  if (isNaN(date.valueOf())) {
    var separator = getSeparator(format);
    var parts = str.split(separator);
    parts = parts.map(function (part) {
      return part.padStart(2, '0');
    });
    str = parts.join(separator);
    date = dayjs(str, format, true).utc(true).toDate(); // May happen when format has time but the srcStr hasn't

    if (isNaN(date.valueOf()) && str.length < format.length) {
      date = dayjs(str, format.substr(0, str.length), true).utc(true).toDate();
    }
  }

  return isNaN(date.valueOf()) ? undefined : date;
};
export var dateToString = function dateToString(date) {
  var format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_DATE_FORMAT;
  if (!date || isNaN(date.getTime())) return '';
  return dayjs.utc(date).format(format);
};
export var dateTimeToString = function dateTimeToString(date, time, format) {
  var dateString = dateToString(date, format);

  if (!time || dateString.length === 0) {
    return dateString;
  }

  var timeString = '';

  if (time.hours !== undefined) {
    timeString += time.hours.toString().padStart(2, '0');

    if (time.minutes !== undefined) {
      timeString += ":".concat(time.minutes.toString().padStart(2, '0'));

      if (time.seconds !== undefined) {
        timeString += ":".concat(time.seconds.toString().padStart(2, '0'));

        if (time.milliseconds !== undefined) {
          timeString += ".".concat(time.milliseconds.toString().padStart(3, '0'));
        }
      }
    }
  }

  return "".concat(dateString, " ").concat(timeString).trim();
};
export var changeDateFormat = function changeDateFormat(str, fromFormat, toFormat) {
  if (!str) return undefined;
  if (!fromFormat || !toFormat) return str;
  var dateString = str;
  var timeString = '';
  var isWithTime = !!toFormat.match(DEFAULT_TIME_FORMAT); // We only need to format date so if there is time, keep it as is

  if (isWithTime) {
    var dateFormat = fromFormat.replace(" ".concat(DEFAULT_TIME_FORMAT), '');

    if (str.length > dateFormat.length) {
      var time = str.split(' ').pop();

      if (time && time.length > 0) {
        timeString += " ".concat(time);
      }
    }

    dateString = str.replace(timeString, '');
  }

  var date = stringToDate(dateString, fromFormat);
  if (!date) return undefined;
  return isWithTime ? dateToString(date, toFormat.replace(" ".concat(DEFAULT_TIME_FORMAT), '')) + timeString : dateToString(date, toFormat);
};
export var getInitialView = function getInitialView(_ref) {
  var initialSelection = _ref.initialSelection,
      initialViewProp = _ref.initialViewProp;

  if (initialSelection) {
    return {
      year: initialSelection.getUTCFullYear(),
      month: initialSelection.getUTCMonth()
    };
  }

  if (initialViewProp) {
    return {
      year: initialViewProp.year,
      // Change from 1-indexed to 0-indexed
      month: initialViewProp.month - 1
    };
  }

  return {
    year: new Date().getUTCFullYear(),
    month: new Date().getUTCMonth()
  };
};
export var getNumDaysInRange = function getNumDaysInRange(start, end) {
  return dayjs.utc(end).diff(dayjs.utc(start), 'day');
};
export var getDateFormatMask = function getDateFormatMask(dateFormat) {
  return dateFormat.replace(/[a-zA-Z]/g, '#');
};